<template>
  <div class="container">
    <div class="box-title" style="display:flex;justify-content: space-between">
      <h4>
        <i class="fal fa-box-check"></i>
        {{ $t('deliveryProof.report_title') }}
        <span class="countTotal"></span>
      </h4>
      <div class="header-actions">
        <!-- A report reading zero can mean the branches are keying everything —
             or that no sender is enabled and nothing is being collected at all.
             That screen is where the difference is. -->
        <button class="button" @click="$router.push({ name: 'delivery.proof.senders' })">
          {{ $t('deliveryProof.enabled_senders_button') }}
        </button>
        <button class="button is-info" @click="$router.push({ name: 'delivery.proof.queue' })">
          {{ $t('deliveryProof.go_to_queue_button') }}
        </button>
      </div>
    </div>

    <div class="box">
      <div class="columns is-multiline">
        <div class="column is-3">
          <label class="label">{{ $t('deliveryProof.from_date') }}:</label>
          <input class="input" type="date" v-model="from" @change="fetchSummary">
        </div>
        <div class="column is-3">
          <label class="label">{{ $t('deliveryProof.to_date') }}:</label>
          <input class="input" type="date" v-model="to" @change="fetchSummary">
        </div>
        <div class="column is-3">
          <label class="label">{{ $t('deliveryProof.branch') }}:</label>
          <!-- The searchable picker the wallet screens use — a branch is chosen
               by name, never by typing an id nobody memorises. -->
          <BranchSelect v-model="branchId" :placeholder="$t('deliveryProof.all')" @input="fetchSummary" />
        </div>
        <div class="column is-3" style="display:flex;align-items:flex-end">
          <button class="button is-primary" @click="fetchSummary">{{ $t('deliveryProof.search_button') }}</button>
        </div>
      </div>
    </div>

    <!-- The window travels with every figure: a compliance number without its
         period attached is the kind of screenshot that gets forwarded and
         misread. -->
    <p class="period-line">
      {{ $t('deliveryProof.period_prefix') }} <strong>{{ from }}</strong> {{ $t('deliveryProof.period_to') }} <strong>{{ to }}</strong>
    </p>

    <p v-if="lastReportError" class="report-error">{{ lastReportError }}</p>

    <div class="center">
      <vs-table>
        <template #thead>
          <vs-tr>
            <vs-th>{{ $t('deliveryProof.branch') }}</vs-th>
            <vs-th>{{ $t('deliveryProof.all') }}</vs-th>
            <vs-th>{{ $t('deliveryProof.status_approved') }}</vs-th>
            <vs-th>{{ $t('deliveryProof.status_rejected') }}</vs-th>
            <vs-th>{{ $t('deliveryProof.status_pending') }}</vs-th>
            <vs-th>{{ $t('deliveryProof.closure_keyed_by_person') }}</vs-th>
            <vs-th>{{ $t('deliveryProof.closure_auto_signed') }}</vs-th>
            <vs-th>{{ $t('deliveryProof.closure_still_open') }}</vs-th>
            <vs-th>{{ $t('deliveryProof.median_lag_hours') }}</vs-th>
          </vs-tr>
        </template>
        <template #tbody>
          <vs-tr v-for="(row, index) in branches" :key="index">
            <vs-td>
              <span class="branch-name">{{ row.branch_name || ('#' + row.branch_id) }}</span>
              <span v-if="row.branch_code" class="branch-code">{{ row.branch_code }}</span>
            </vs-td>
            <!-- Total first, then its buckets. No review outcome reduces it. -->
            <vs-td><strong>{{ row.total }}</strong></vs-td>
            <vs-td class="clickable" @click.native="openRows(row, 'approved')">
              {{ row.confirmed }}
            </vs-td>
            <vs-td class="clickable" @click.native="openRows(row, 'rejected')">
              {{ row.disputed }}
            </vs-td>
            <vs-td class="clickable" @click.native="openRows(row, 'pending')">
              {{ row.unreviewed }}
            </vs-td>
            <vs-td>{{ row.closure.keyed_by_person }}</vs-td>
            <!-- Its own column, never folded into "keyed": a branch that lets
                 auto-sign close everything reads worse than one with bills still
                 open, and that distinction is the point of the report. -->
            <vs-td class="auto-signed">{{ row.closure.auto_signed }}</vs-td>
            <vs-td>{{ row.closure.open }}</vs-td>
            <vs-td>{{ medianLabel(row) }}</vs-td>
          </vs-tr>
        </template>
      </vs-table>

      <p v-if="!loading && branches.length === 0" class="empty-line">
        {{ $t('deliveryProof.empty_period') }}
      </p>
    </div>
  </div>
</template>

<script>
import DeliveryProofApi from '@/Mixins/DeliveryProof/DeliveryProofApi';
import BranchSelect from '@/components/BranchSelect.vue';

/**
 * Per-branch delivery-proof compliance.
 *
 * Every figure is rendered as the API returned it. Nothing on this screen
 * recomputes a total, a median or a closure count — an aggregate derived from
 * one page would be the median of that page, and would disagree with the same
 * number elsewhere without ever looking wrong.
 */
export default {
    name: 'DeliveryProofReport',
    components: { BranchSelect },
    mixins: [DeliveryProofApi],

    /**
     * Below this many proofs a branch's row shows counts and NO median. Three
     * proofs cannot produce a figure anyone should act on, and a median over
     * them invites exactly that.
     */
    MIN_PROOFS_FOR_MEDIAN: 5,

    data() {
        const today = new Date();
        const first = new Date(today.getFullYear(), today.getMonth(), 1);

        return {
            from: first.toISOString().slice(0, 10),
            to: today.toISOString().slice(0, 10),
            branchId: '',
            branches: [],
            loading: false,
        };
    },

    mounted() {
        this.fetchSummary();
    },

    methods: {
        fetchSummary() {
            this.loading = true;
            this.lastReportError = null;

            const params = { from: this.from, to: this.to };
            if (this.branchId) params.branch_id = this.branchId;

            this.deliveryProofSummary(params)
                .then(({ data }) => {
                    this.branches = data.data.branches || [];
                })
                .catch((error) => {
                    this.branches = [];
                    this.handleReportError(error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        /**
         * A bill nobody keyed has an AGE, not a delay — so an empty median is
         * rendered as "no data", never as `0`. A zero would read as "no delay",
         * which is the opposite of the truth for the worst rows in the table.
         */
        medianLabel(row) {
            if (row.total < this.$options.MIN_PROOFS_FOR_MEDIAN) return '—';
            if (row.median_lag_hours === null || row.median_lag_hours === undefined) {
                return this.$t('deliveryProof.closure_open');
            }
            return Number(row.median_lag_hours).toFixed(1);
        },

        openRows(row, status) {
            this.$router.push({
                name: 'delivery.proof.rows',
                query: {
                    branch_id: row.branch_id,
                    // Carried so the drill-down can name the branch in its own
                    // header without a second lookup.
                    branch_name: row.branch_name || '',
                    status,
                    from: this.from,
                    to: this.to,
                },
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.period-line {
    margin: 8px 0 12px;
    font-size: 14px;
}

.header-actions {
    display: flex;
    gap: 8px;
}

.report-error {
    margin-bottom: 12px;
    padding: 10px;
    background: #fdecea;
    color: #b71c1c;
    border-radius: 4px;
}

.clickable {
    cursor: pointer;
    text-decoration: underline;
}

.auto-signed {
    font-weight: 600;
}

.branch-name {
    display: block;
    font-weight: 600;
}

.branch-code {
    display: block;
    font-size: 12px;
    color: #777;
}

.empty-line {
    padding: 24px 0;
    text-align: center;
    color: #666;
}
</style>
